@if (errorMessage) {
  <tidy-error-message
    [text]="errorMessage" />
}

@if (windowService.isDesktopRes) {
  <tidy-desktop-header #reviewsHeader>
    <div class="wrappable-container">
      <tidy-text
        [header]="'Reviews'" />
      <form [formGroup]="searchForm" novalidate action="#">
        <tidy-search-input
          formControlName="search"
          [placeholder]="'Search'"
          [icon]="'assets/img/search-header.svg'"
          class="header-search" />
      </form>
    </div>
    <div class="non-wrappable-right-aligned-container">
      <automations-preview-button
        [tag]="'reviews'" />
    </div>
  </tidy-desktop-header>

  <tidy-sub-tabs
    [tabs]="viewTabs"
    [activeTab]="selectedView"
    (tabChange)="onViewChange($event)" />

  @if (selectedView === 'inbound') {
    <!-- Skeleton table while loading — rows float in with the entrance cascade;
         the real table below then replaces it in place, instantly (skipEntrance). -->
    @if (!isDataLoaded) {
      <tidy-table
        [skeletonLoading]="true"
        [columnslabels]="headers" />
    }

    @if (isDataLoaded && rows.length > 0) {
      <tidy-table
        [dataSource]="rows"
        [displayedColumns]="headers"
        [columnslabels]="headers"
        [showColumnLines]="false"
        [rowsToShow]="50"
        [allRowIsClickable]="true"
        [showArrowIcon]="true"
        [skipEntrance]="true"
        (sortChange)="sortChanged($event)"
        (cellClick)="onReviewCellClick($event)"
        [infiniteScroll]="true"
        [totalRows]="rows.length" />
    }

    @if (isDataLoaded && rows.length === 0) {
      <tidy-alert>
        <tidy-row>
          <tidy-text [body]="hasReviews ? 'No reviews match your search.' : emptyStateMessage" />
        </tidy-row>
      </tidy-alert>
    }
  }

  @if (selectedView === 'expected') {
    @if (!isExpectedLoaded) {
      <tidy-table
        [skeletonLoading]="true"
        [columnslabels]="expectedHeaders" />
    }

    @if (isExpectedLoaded && expectedRows.length > 0) {
      <tidy-table
        [dataSource]="expectedRows"
        [displayedColumns]="expectedHeaders"
        [columnslabels]="expectedHeaders"
        [showColumnLines]="false"
        [rowsToShow]="50"
        [allRowIsClickable]="true"
        [showArrowIcon]="true"
        [skipEntrance]="true"
        (sortChange)="expectedSortChanged($event)"
        (cellClick)="onExpectedCellClick($event)"
        [infiniteScroll]="true"
        [totalRows]="expectedRows.length" />
    }

    @if (isExpectedLoaded && expectedRows.length === 0) {
      <tidy-alert>
        <tidy-row>
          <tidy-text [body]="hasExpectedReviews ? 'No reservations match your search.' : expectedEmptyStateMessage" />
        </tidy-row>
      </tidy-alert>
    }
  }
}

@if (!windowService.isDesktopRes) {
  <tidy-sub-tabs
    [tabs]="viewTabs"
    [activeTab]="selectedView"
    (tabChange)="onViewChange($event)" />

  @if (selectedView === 'inbound') {
    @if (isDataLoaded) {
      @for (card of reviewCards; track card.id) {
        <tidy-card class="reviews-card--clickable" (click)="openReviewDetail(card.id)">
          <tidy-row [alignLastItemRight]="true">
            <tidy-text
              [header]="card.listingName"
              [translate]="false" />
            <tidy-text
              [helper]="card.channel"
              [translate]="false" />
          </tidy-row>
          <tidy-row class="extra-top-padding">
            <tidy-grid>
              <ion-row>
                <ion-col size="6">
                  <tidy-row>
                    <tidy-text [helper]="'Guest'" />
                  </tidy-row>
                  <tidy-row>
                    <tidy-text
                      [body]="card.guestName"
                      [translate]="false" />
                  </tidy-row>
                </ion-col>
                <ion-col size="6">
                  <tidy-row>
                    <tidy-text [helper]="'Rating'" />
                  </tidy-row>
                  <tidy-row>
                    <tidy-image [src]="card.iconSrc" class="reviews-card__star" />
                    <tidy-text
                      [body]="card.ratingLabel"
                      [translate]="false" />
                  </tidy-row>
                </ion-col>
              </ion-row>
            </tidy-grid>
          </tidy-row>
          @if (card.subRatingsSummary) {
            <tidy-row class="extra-top-padding">
              <tidy-text
                [helper]="card.subRatingsSummary"
                [translate]="false" />
            </tidy-row>
          }
          <tidy-row class="extra-top-padding">
            <span class="reviews-status-pill" [ngClass]="card.responsePillClass">{{ card.responseLabel }}</span>
          </tidy-row>
          @if (card.stayDates) {
            <tidy-row class="extra-top-padding">
              <tidy-text
                [helper]="'Stay: ' + card.stayDates"
                [translate]="false" />
            </tidy-row>
          }
          <tidy-row class="extra-top-padding">
            <tidy-text
              [helper]="'Review: ' + card.comment"
              [translate]="false" />
          </tidy-row>
        </tidy-card>
      }
      @if (reviewCards.length === 0) {
        <tidy-alert>
          <tidy-row>
            <tidy-text [body]="hasReviews ? 'No reviews match your search.' : emptyStateMessage" />
          </tidy-row>
        </tidy-alert>
      }
    }
  }

  @if (selectedView === 'expected') {
    @if (isExpectedLoaded) {
      @for (card of expectedCards; track card.id) {
        <tidy-card class="reviews-card--clickable" (click)="openLeaveReview(card.id)">
          <tidy-row [alignLastItemRight]="true">
            <tidy-text
              [header]="card.listingName"
              [translate]="false" />
            <tidy-text
              [helper]="card.channel"
              [translate]="false" />
          </tidy-row>
          <tidy-row class="extra-top-padding">
            <tidy-grid>
              <ion-row>
                <ion-col size="6">
                  <tidy-row>
                    <tidy-text [helper]="'Guest'" />
                  </tidy-row>
                  <tidy-row>
                    <tidy-text
                      [body]="card.guestName || '—'"
                      [translate]="false" />
                  </tidy-row>
                </ion-col>
                <ion-col size="6">
                  <tidy-row>
                    <tidy-text [helper]="'Status'" />
                  </tidy-row>
                  <tidy-row>
                    <span class="reviews-status-pill" [ngClass]="card.statusPillClass">{{ card.statusLabel }}</span>
                  </tidy-row>
                </ion-col>
              </ion-row>
            </tidy-grid>
          </tidy-row>
          @if (card.stayDates) {
            <tidy-row class="extra-top-padding">
              <tidy-text
                [helper]="'Stay: ' + card.stayDates"
                [translate]="false" />
            </tidy-row>
          }
        </tidy-card>
      }
      @if (expectedCards.length === 0) {
        <tidy-alert>
          <tidy-row>
            <tidy-text [body]="hasExpectedReviews ? 'No reservations match your search.' : expectedEmptyStateMessage" />
          </tidy-row>
        </tidy-alert>
      }
    }
  }
}

@if (showAboutThisPage) {
  <tidy-about-this-page [page]="'reviews'" [overlayTop]="overlayTop" (dismissed)="dismissAboutThisPage()" />
}
